import React from 'react';
import { Accordion, Checkbox, Dropdown, Icon, Segment, Popup } from 'semantic-ui-react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { FeedParams } from '../../app/models/Playground';

interface Props {
  params: FeedParams;
  onParamChange: (p: Partial<FeedParams>) => void;
}

const InfoTip = ({ content }: { content: string }) => (
  <Popup
    content={content}
    trigger={
      <Icon
        name='info circle'
        color='grey'
        size='small'
        style={{ marginLeft: 5, cursor: 'pointer', verticalAlign: 'middle' }}
      />
    }
    position='top center'
    size='small'
    inverted
  />
);

export const FeedFilters = observer(({ params, onParamChange }: Props) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const radiusOptions = [1, 5, 10, 20, 30, 50].map(v => ({
    key: v, value: v, text: `${v} km`,
  }));

  const filterItems = [
    {
      key: 'showNeedOnePlayer',
      label: t('playground.filter.needOnePlayer'),
      info: t('playground.filter.needOnePlayer.info', { radius: params.radiusKm }),
    },
    {
      key: 'showPublicGames',
      label: t('playground.filter.publicGames'),
      info: t('playground.filter.publicGames.info', { radius: params.radiusKm }),
    },
    {
      key: 'showFriendActivity',
      label: t('playground.filter.friendActivity'),
      info: t('playground.filter.friendActivity.info'),
    },
    {
      key: 'showUserPosts',
      label: t('playground.filter.userPosts'),
      info: t('playground.filter.userPosts.info'),
    },
  
    {
      key: 'showTournaments',
      label: t('playground.filter.tournaments'),
      info: t('playground.filter.tournaments.info', { radius: params.radiusKm }),
    },
  ];

  return (
    <Accordion as={Segment} style={{ marginBottom: 12, borderRadius: 10, padding: '8px 14px' }}>
      <Accordion.Title active={open} onClick={() => setOpen(!open)} style={{ fontWeight: 600 }}>
        <Icon name='filter' /> {t('playground.filters')}
        <Icon name={open ? 'chevron up' : 'chevron down'} style={{ float: 'right' }} />
      </Accordion.Title>
      <Accordion.Content active={open}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', paddingTop: 10 }}>

          {/* ── Radius ── */}
          <div>
            <label style={{ fontWeight: 600, fontSize: '0.85em', display: 'block', marginBottom: 4 }}>
              {t('playground.radius')}
              <InfoTip content={t('playground.radius.info')} />
            </label>
            <Dropdown
              selection
              compact
              options={radiusOptions}
              value={params.radiusKm}
              onChange={(_, d) => onParamChange({ radiusKm: d.value as number })}
            />
          </div>

          {/* ── Show/hide filters ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <label style={{ fontWeight: 600, fontSize: '0.85em' }}>{t('playground.show')}</label>
            {filterItems.map(({ key, label, info }) => (
              <div key={key} style={{ display: 'flex', alignItems: 'center' }}>
                <Checkbox
                  label={label}
                  checked={params[key as keyof FeedParams] as boolean}
                  onChange={(_, d) => onParamChange({ [key]: d.checked })}
                />
                <InfoTip content={info} />
              </div>
            ))}
          </div>

          {/* ── Friends only toggle ── */}
          <div>
            <label style={{ fontWeight: 600, fontSize: '0.85em', display: 'block', marginBottom: 4 }}>
              {t('playground.audience')}
              <InfoTip content={t('playground.friendsOnly.info')} />
            </label>
            <Checkbox
              toggle
              label={t('playground.friendsOnly')}
              checked={params.friendsOnly}
              onChange={(_, d) => onParamChange({ friendsOnly: d.checked })}
            />
          </div>

        </div>
      </Accordion.Content>
    </Accordion>
  );
});